import { RefreshCw, Trash2, History as HistoryIcon } from "lucide-react";
import { removeFromHistory, clearHistory } from "@/lib/history";
import { toast } from "sonner";

export default function History({ items, onRerun, setHistory }) {
  if (!items || items.length === 0) return null;

  const handleRemove = (id) => {
    const next = removeFromHistory(id);
    setHistory(next);
  };

  const handleClearAll = () => {
    clearHistory();
    setHistory([]);
    toast.success("History cleared");
  };

  return (
    <section
      id="history"
      data-testid="history-section"
      className="relative py-16 md:py-24 border-t border-zinc-200 dark:border-zinc-800"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-yellow-500 mb-2 flex items-center gap-2">
              <HistoryIcon className="w-3.5 h-3.5" />
              // Local archive
            </p>
            <h2 className="font-display font-bold text-3xl md:text-5xl text-zinc-900 dark:text-white tracking-tight">
              Your recent grabs.
            </h2>
            <p className="mt-3 text-zinc-400 dark:text-zinc-600 dark:text-zinc-400 max-w-xl">
              Saved right in your browser. No account, no cloud, no snooping —
              just your private swipe file.
            </p>
          </div>
          <button
            data-testid="clear-history-btn"
            onClick={handleClearAll}
            className="inline-flex items-center gap-2 text-sm text-zinc-400 dark:text-zinc-600 dark:text-zinc-400 hover:text-yellow-500 border border-zinc-200 dark:border-zinc-800 hover:border-yellow-500 px-4 h-10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Clear all
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <article
              key={item.id}
              data-testid={`history-item-${item.id}`}
              className="border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex flex-col group hover:border-yellow-500 transition-colors"
            >
              <div className="relative aspect-video bg-black overflow-hidden">
                {item.preview_image_url ? (
                  <img
                    src={item.preview_image_url}
                    alt={item.page_name || "Ad thumbnail"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-zinc-300 dark:text-zinc-700 font-mono text-xs uppercase tracking-widest">
                    No preview
                  </div>
                )}
                <div className="absolute top-2 left-2 bg-white/80 dark:bg-black/80 backdrop-blur-sm px-2 py-1 border border-zinc-200 dark:border-zinc-800">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-yellow-500">
                    {item.video_hd_url ? "HD" : item.video_sd_url ? "SD" : "IMG"}
                  </span>
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h4 className="font-display font-bold text-zinc-900 dark:text-white text-base leading-tight line-clamp-1">
                  {item.page_name || "Unknown"}
                </h4>
                <p className="mt-1 text-xs font-mono text-zinc-500 dark:text-zinc-500">
                  {new Date(item.saved_at).toLocaleString()}
                </p>
                <div className="flex-1" />
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => onRerun(item.source_url)}
                    data-testid={`history-rerun-${item.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-sm h-9 transition-all hover:-translate-y-0.5"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Re-grab
                  </button>
                  <button
                    onClick={() => handleRemove(item.id)}
                    data-testid={`history-remove-${item.id}`}
                    className="h-9 w-9 grid place-items-center border border-zinc-200 dark:border-zinc-800 hover:border-yellow-500 text-zinc-400 dark:text-zinc-600 dark:text-zinc-400 hover:text-yellow-500 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
